export type FooterPlaceholder = {
  init(): void;
};

// Keeps the placeholder as tall as the fixed footer, so page content can scroll above it.
// A footer taller than the viewport is pinned to the top instead, and once the page content
// has scrolled out of view, the footer scrolls along with the page to reveal the rest of it.
function footerPlaceholder(): FooterPlaceholder {
  return {
    init() {
      const placeholder = this.$el as HTMLElement;
      const footer = document.getElementById('site-footer');
      if (!footer) return;

      let isTall = false;

      const setOffset = () => {
        if (!isTall) return;

        const overflow = footer.offsetHeight - window.innerHeight;
        const scrolledPast = -placeholder.getBoundingClientRect().top;
        const offset = Math.min(Math.max(scrolledPast, 0), overflow);

        footer.style.transform = `translateY(${-offset}px)`;
      };

      const setHeight = () => {
        const footerHeight = footer.offsetHeight;
        isTall = footerHeight > window.innerHeight;

        // The placeholder matches the footer in both cases: in tall mode the first viewport
        // height reveals the pinned footer, the remainder scrolls the footer itself
        placeholder.style.height = `${footerHeight}px`;

        if (isTall) {
          footer.style.top = '0';
          footer.style.bottom = 'auto';
          setOffset();
        } else {
          footer.style.top = '';
          footer.style.bottom = '';
          footer.style.transform = '';
        }
      };

      // A fixed footer counts as already in view, so the browser won't scroll to focused
      // elements inside it, leaving them hidden behind the page content. Scroll manually instead.
      const revealFocused = (event: FocusEvent) => {
        const target = event.target as HTMLElement;
        const padding = 180;
        const targetRect = target.getBoundingClientRect();
        const placeholderTop = placeholder.getBoundingClientRect().top;
        let delta = 0;

        if (isTall) {
          // Position within the footer, unaffected by the current translateY
          const top = targetRect.top - footer.getBoundingClientRect().top;
          const bottom = top + targetRect.height;
          const overflow = footer.offsetHeight - window.innerHeight;
          const current = -placeholderTop;
          let next = Math.max(current, 0);

          if (top < next) next = top - padding;
          else if (bottom > next + window.innerHeight)
            next = bottom - window.innerHeight + padding;

          delta = Math.min(Math.max(next, 0), overflow) - current;
        } else {
          // The footer is visible below the placeholder's top edge
          delta = Math.max(placeholderTop - (targetRect.top - padding), 0);
        }

        if (delta !== 0) window.scrollBy({ top: delta });
      };

      // Re-measures when footer content expands/collapses and when the viewport changes
      new ResizeObserver(setHeight).observe(footer);
      window.addEventListener('resize', setHeight);
      window.addEventListener('scroll', setOffset, { passive: true });
      footer.addEventListener('focusin', revealFocused);
      setHeight();
    },
  };
}

window.footerPlaceholder = footerPlaceholder;
